import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Users, Settings, GraduationCap, Shield, Building2, Heart, CheckCircle, Briefcase, TrendingUp, Lightbulb, Compass } from 'lucide-react';
import { motion } from 'framer-motion';

export const HomePage = () => {
  // Legacy Leadership System Pillars
  const systemPillars = [
    { icon: Users, name: 'Executive Leadership', color: '#4F8A2F' },
    { icon: Target, name: 'District Systems', color: '#0B1F3A' },
    { icon: Building2, name: 'School Performance', color: '#172033' },
    { icon: Settings, name: 'Workforce & Innovation', color: '#4F8A2F' },
    { icon: Shield, name: 'Governance', color: '#0B1F3A' },
    { icon: Heart, name: 'Safety & Culture', color: '#172033' },
    { icon: GraduationCap, name: 'Legacy', color: '#4F8A2F' },
  ];

  // Flagship Programs
  const flagshipPrograms = [
    {
      title: 'Legacy District Impact Accelerator™',
      desc: 'Our comprehensive district transformation program that integrates all seven pillars of the Legacy Leadership System.',
      tag: 'FLAGSHIP',
      featured: true,
    },
    {
      title: 'Legacy Executive Cabinet Academy™',
      desc: 'Develop your district\'s senior leadership team into a high-performing executive cabinet.',
      tag: 'LEADERSHIP',
    },
    {
      title: 'Governance in Alignment™',
      desc: 'Strengthen board-superintendent relationships and create governance that supports student success.',
      tag: 'GOVERNANCE',
    },
    {
      title: '100% Ready Initiative™',
      desc: 'Achieve 100% College & Career Readiness through systematic implementation and accountability.',
      tag: 'CCRI',
    },
  ];

  // Process Steps
  const processSteps = [
    { step: '01', title: 'Discover', desc: 'District Assessment' },
    { step: '02', title: 'Design', desc: 'Strategic Roadmap' },
    { step: '03', title: 'Develop', desc: 'Leadership Capacity' },
    { step: '04', title: 'Deploy', desc: 'Implementation Support' },
    { step: '05', title: 'Demonstrate', desc: 'Measure Results' },
  ];

  // Results
  const results = [
    { metric: '99%', label: 'Graduation Rate Achieved' },
    { metric: '99%', label: 'College & Career Readiness' },
    { metric: '1 Year', label: 'School Turnaround' },
    { metric: '25+', label: 'Years of Leadership' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1517502884422-41eaead166d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBsZWFkZXJzaGlwJTIwYm9hcmRyb29tJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODM4Njg4ODF8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="container-premium relative z-10 pt-32 pb-20">
          <div className="max-w-4xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="overline text-[#6FAE3A] mb-6"
            >
              Lead. Develop. Empower. Impact.
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-8"
            >
              Building Leaders.<br />
              <span className="text-[#6FAE3A]">Creating Legacy.</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-slate-200 text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl"
            >
              Leadership development, professional learning, coaching, and strategic solutions for schools, organizations, and businesses. We partner with you to strengthen leadership, improve systems, and create measurable impact that lasts.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/programs" data-testid="hero-cta-primary">
                <Button className="bg-[#4F8A2F] hover:bg-[#6FAE3A] text-white rounded-full px-8 py-5 font-body font-semibold text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  Explore Our Services
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-cta-secondary">
                <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-5 font-body text-base">
                  Partner With Us
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Core Service Areas Section */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="services-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline mb-4">Who We Serve</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A] mb-6">
              Core Service Areas
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
              Comprehensive leadership solutions for schools, organizations, and businesses seeking sustainable growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {[
              { icon: GraduationCap, title: 'Education Leadership', desc: 'District transformation and school leadership development', color: '#0B1F3A' },
              { icon: Briefcase, title: 'Business & Organizational Solutions', desc: 'Executive consulting for corporations and organizations', color: '#4F8A2F' },
              { icon: Lightbulb, title: 'Professional Development', desc: 'Learning experiences that build capacity and drive results', color: '#0B1F3A' },
              { icon: TrendingUp, title: 'Strategic Growth', desc: 'Strategic planning and organizational advancement', color: '#4F8A2F' },
              { icon: Compass, title: 'Coaching & Consulting', desc: 'Executive coaching and advisory partnerships', color: '#0B1F3A' },
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-premium hover-lift text-center"
                data-testid={`core-service-${index}`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-5"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color }} strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-lg font-bold text-[#0B1F3A] mb-2">{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy Leadership System Section */}
      <section className="section-padding bg-white" data-testid="system-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline mb-4">Our Framework</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              The Legacy Leadership System™
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
              A comprehensive framework for district transformation built on seven integrated pillars of organizational excellence.
            </p>
          </div>

          {/* System Pillars Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 lg:gap-6">
            {systemPillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-premium text-center p-6 hover-lift"
                data-testid={`pillar-${index}`}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${pillar.color}15` }}
                >
                  <pillar.icon className="w-6 h-6" style={{ color: pillar.color }} strokeWidth={1.5} />
                </div>
                <h3 className="font-body text-sm font-semibold text-[#172033]">
                  {pillar.name}
                </h3>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs" data-testid="system-cta">
              <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-full px-8 py-4 font-body">
                Explore the Full System
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Flagship Programs Section */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="programs-section">
        <div className="container-premium">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">Signature Programs</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Flagship Offerings
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Eight signature programs designed to transform every aspect of district leadership and student outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {flagshipPrograms.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`card-premium hover-lift ${program.featured ? 'md:col-span-2 bg-[#0B1F3A] text-white' : ''}`}
                data-testid={`program-card-${index}`}
              >
                <div className="flex flex-col h-full">
                  <span className={`text-xs font-semibold tracking-wider uppercase mb-4 ${program.featured ? 'text-[#4F8A2F]' : 'text-[#4F8A2F]'}`}>
                    {program.tag}
                  </span>
                  <h3 className={`font-display text-2xl lg:text-3xl font-bold mb-4 ${program.featured ? 'text-white' : 'text-[#172033]'}`}>
                    {program.title}
                  </h3>
                  <p className={`text-base leading-relaxed mb-6 flex-grow ${program.featured ? 'text-slate-300' : 'text-slate-600'}`}>
                    {program.desc}
                  </p>
                  <Link to={program.featured ? '/accelerator' : '/programs'}>
                    <Button 
                      variant={program.featured ? 'default' : 'outline'}
                      className={`rounded-full font-body ${
                        program.featured 
                          ? 'bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A]' 
                          : 'border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white'
                      }`}
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs">
              <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-8 py-4 font-body">
                View All Programs
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline mb-4">Our Approach</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              District Transformation Process
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
              A proven five-phase approach to creating sustainable change in school districts.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="w-16 h-16 bg-[#0B1F3A] text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-xl font-bold">{step.step}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-[#172033] mb-2">{step.title}</h3>
                <p className="text-slate-500 text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="results-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline text-[#4F8A2F] mb-4">Proven Impact</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Results That Matter
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
              Measurable outcomes from our partnership with school districts across the country.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {results.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl bg-white/5 border border-white/10"
                data-testid={`result-${index}`}
              >
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">
                  {result.metric}
                </p>
                <p className="text-slate-300 text-sm">{result.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-padding bg-white" data-testid="about-preview">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="overline mb-4">About the Founder</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
                Cameron Whitlow
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                With over 25 years of educational leadership experience, Captain Cameron Whitlow brings a unique combination of military discipline, executive expertise, and proven results to district transformation.
              </p>
              <ul className="space-y-3 mb-8">
                {['Superintendent-level Cabinet Experience', 'Turnaround Principal', 'District Director', 'CTE Director', 'Military Officer', 'National Speaker'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="w-5 h-5 text-[#4F8A2F]" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-full px-8 py-4 font-body">
                  Read Full Bio
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instructional-lead/artifacts/eyi4u393_83285.jpg"
                  alt="Cameron Whitlow"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#0B1F3A] text-white p-6 rounded-2xl hidden md:block">
                <p className="font-display text-3xl font-bold text-[#4F8A2F]">25+</p>
                <p className="text-sm text-slate-300">Years of Leadership</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
